import MuiMenuItem, {type MenuItemProps as MuiMenuItemProps} from '@mui/material/MenuItem';
import {css, styled} from '@mui/material/styles';
import clsx from 'clsx';
import {ComponentPropsWithoutRef, forwardRef} from 'react';
import {ButtonProps} from '../Button';

type PickedAnchorProps = Partial<Pick<ComponentPropsWithoutRef<'a'>, 'href'>>;
type PickedButtonProps = Partial<Pick<ButtonProps, 'startIcon' | 'endIcon'>>;
type PickedMenuItemProps = MuiMenuItemProps<'li', unknown>;

export type MenuItemProps = PickedMenuItemProps & PickedButtonProps & PickedAnchorProps;

const StyledMenuItem = styled(MuiMenuItem)(({theme}) => css`
    padding: unset !important;
    background-color: unset !important;
    text-decoration: unset !important;
    outline: unset !important;

    position: relative;
    width: 100%;

    .MenuItem-link {
        border: unset;
        background: unset;
        text-decoration: unset;

        cursor: pointer;
        position: relative;
        color: ${theme.tokens.FuelColorTextPrimary};
        font-family: ${theme.tokens.FuelFontFamilyBase};
        font-size: ${theme.tokens.FuelFontSizeSm};
        line-height: ${theme.tokens.FuelLineHeightBase};
        text-align: start;

        width: 100%;
        display: flex;
        align-items: center;
        gap: ${theme.tokens.FuelSpacing2};
        padding-inline: ${theme.tokens.FuelSpacing4};
        padding-block: ${theme.tokens.FuelSpacing2};
    }

    &[aria-expanded=true] .MenuItem-link {
        color: ${theme.tokens.FuelColorBrandPrimary};
        background-color: ${theme.tokens.FuelColorBackgroundActionDefaultSelected};

        &::after {
            content: "";
            position: absolute;
            width: 3px;
            height: 100%;
            left: 0;
            top: 0;
            background-color: ${theme.tokens.FuelColorBackgroundActionDefaultSelectedBar};
        }
    }

    &:hover .MenuItem-link {
        background-color: ${theme.tokens.FuelColorBackgroundActionDefaultHovered};
    }

    &:active .MenuItem-link {
        background-color: ${theme.tokens.FuelColorBackgroundActionDefaultPressed};
    }

    &:focus-visible .MenuItem-link {
        background-color: ${theme.tokens.FuelColorBackgroundActionDefaultHovered};
        box-shadow: inset 0 0 0 2px ${theme.tokens.FuelColorFocused};
    }
`);

const MenuItemText = styled('span')`
    flex-grow: 1;
    display: inline-block;
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
`;

export const MenuItem = forwardRef<HTMLLIElement, MenuItemProps>(({
    children, onClick, endIcon, href, startIcon, LinkComponent = 'a',
    selected, className, ...props
}, ref) => {
    return (
        <StyledMenuItem
            {...props}
            aria-expanded={selected}
            className={clsx('MenuItem-root', className)}
            onClick={onClick}
            ref={ref}
        >
            <LinkComponent
                className='MenuItem-link'
                href={href}
            >
                {startIcon}
                <MenuItemText className='MenuItem-text'>{children}</MenuItemText>
                {endIcon}
            </LinkComponent>
        </StyledMenuItem>
    );
});

MenuItem.displayName = 'MenuItem';
